const BASE = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'adintel_token';

// DopScale API (auth provider, invitations) — chamado direto
const DS_API = 'https://staging-api.dopscale.com';

/** Fetch contra o DopScale API (auth + invitations). Usa mesmo JWT que useAuth guarda. */
export async function dsFetch(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${DS_API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `DopScale ${res.status}`);
  }
  return data;
}

export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (res.status === 401) {
    // Token invalido/expirado — limpa e redireciona pro login
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('adintel_user');
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(typeof error.detail === 'string' ? error.detail : `API ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

export const api = {
  getAds: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiFetch(`/api/ads?${q}`);
  },
  getAdStats: (period = '7d') => apiFetch(`/api/ads/stats?period=${period}`),
  getActiveSession: () => apiFetch('/api/sessions/active'),
  getProfiles: () => apiFetch('/api/profiles'),

  // Bot Instances
  getInstances: () => apiFetch('/api/bot/instances'),
  createInstance: (data) => apiFetch('/api/bot/instances', { method: 'POST', body: JSON.stringify(data) }),
  getInstance: (id) => apiFetch(`/api/bot/instances/${id}`),
  updateInstance: (id, data) => apiFetch(`/api/bot/instances/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInstance: (id) => apiFetch(`/api/bot/instances/${id}`, { method: 'DELETE' }),
  startInstance: (id) => apiFetch(`/api/bot/instances/${id}/start`, { method: 'POST' }),
  stopInstance: (id) => apiFetch(`/api/bot/instances/${id}/stop`, { method: 'POST' }),
  getInstanceLogs: (id, lines = 100) => apiFetch(`/api/bot/instances/${id}/logs?lines=${lines}`),
  getInstanceStatus: (id) => apiFetch(`/api/bot/instances/${id}/status`),

  // Live logs
  getLiveLogs: (lines = 200) => apiFetch(`/api/bot/live-logs?lines=${lines}`),

  // Diagnostics
  getDiagnosticsSummary: () => apiFetch('/api/diagnostics/summary'),
  getDiagnostics: (params = {}) => { const q = new URLSearchParams(params).toString(); return apiFetch(`/api/diagnostics?${q}`); },
  reviewDiagnostic: (date, folder) => apiFetch(`/api/diagnostics/${date}/${folder}/review`, { method: 'POST' }),

  // Clone Site
  cloneSiteList: (offerId, limit = 50) => {
    const params = { limit };
    if (offerId) params.offer_id = offerId;
    const q = new URLSearchParams(params).toString();
    return apiFetch(`/api/clone-site?${q}`);
  },
  cloneSiteCreate: (url, offerId) => apiFetch('/api/clone-site', {
    method: 'POST',
    body: JSON.stringify({ url, offer_id: offerId || null }),
  }),
  cloneSiteGet: (id) => apiFetch(`/api/clone-site/${id}`),
};

// Download autenticado (ZIP / MD) — usa Blob, salva no disco do usuario
export async function downloadCloneAsset(jobId, kind = 'download', filename = null) {
  const token = localStorage.getItem('adintel_token');
  const res = await fetch(`${BASE}/api/clone-site/${jobId}/${kind}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`Download falhou: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `clone-${jobId.slice(0, 8)}.${kind === 'instructions' ? 'md' : 'zip'}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadLibraryArchive(jobId) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BASE}/api/ad-library-import/${jobId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download falhou: ${res.status}. Atualize a página e tente novamente.`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `anuncios-${jobId.slice(0, 8)}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
